import { useEffect, useState } from "react";
import { Send, Users, Tags, UserCheck, Loader2, Eye, CalendarClock, Megaphone } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useApi } from "@/hooks/useApi";
import { PageHeader, LoadingGrid, ErrorState, EmptyState, StatusBadge, Pager, UploadButton, Field, Confirm } from "@/components/common";
import { Sel, Table, td } from "@/components/admin-ui";
import { CreatorPicker } from "@/components/CreatorPicker";
import { useBranding } from "@/context/BrandingContext";
import { api, errMsg, fileUrl } from "@/lib/api";
import { ftime } from "@/lib/format";
import { cn } from "@/lib/utils";

const TARGETS = [["all", "All approved creators", Users], ["category", "One category", Tags], ["selected", "Selected creators", UserCheck]];

function Composer({ onSent }) {
  const { branding } = useBranding();
  const [cats, setCats] = useState([]);
  const [f, setF] = useState({ target_type: "all", category_id: "", title: "", message: "", image: "", link: "", scheduled_at: "" });
  const [picked, setPicked] = useState([]);
  const [count, setCount] = useState(null);
  const [preview, setPreview] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)).catch(() => {}); }, []);
  const audience = { target_type: f.target_type, category_id: f.category_id, creator_ids: picked.map((p) => p.user_id) };
  const audKey = JSON.stringify(audience);
  useEffect(() => {
    setCount(null);
    const a = JSON.parse(audKey);
    if ((a.target_type === "category" && !a.category_id) || (a.target_type === "selected" && !a.creator_ids.length)) { setCount(0); return; }
    api.post("/admin/broadcasts/preview", a).then((r) => setCount(r.data.recipient_count)).catch(() => setCount(0));
  }, [audKey]);
  const send = async () => {
    setBusy(true);
    try {
      const body = { ...f, ...audience, scheduled_at: f.scheduled_at ? new Date(f.scheduled_at).toISOString() : "" };
      const { data } = await api.post("/admin/broadcasts", body);
      toast.success(data.status === "SCHEDULED" ? "Broadcast scheduled" : `Sent to ${data.recipient_count} creators`);
      setF({ ...f, title: "", message: "", image: "", link: "", scheduled_at: "" }); setPicked([]); setPreview(false);
      onSent();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const btnLabel = f.scheduled_at ? "Schedule" : f.target_type === "category" ? "Send to category" : f.target_type === "selected" ? "Send to selected" : "Send to all";
  const canSend = f.title.trim() && f.message.trim() && count > 0;
  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <div className="lg:col-span-3 uc-card p-6 space-y-5" data-testid="broadcast-composer">
        <div className="grid sm:grid-cols-3 gap-2">
          {TARGETS.map(([v, l, Icon]) => (
            <button key={v} type="button" onClick={() => setF({ ...f, target_type: v })} data-testid={`broadcast-target-${v}`}
              className={cn("rounded-xl border p-3.5 text-left text-sm transition-colors", f.target_type === v ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary/40")}>
              <Icon className="h-4 w-4 mb-2" />{l}
            </button>
          ))}
        </div>
        {f.target_type === "category" && <Sel value={f.category_id} onChange={(v) => setF({ ...f, category_id: v })} options={cats.map((c) => [c.id, c.name])} placeholder="Choose category" testid="broadcast-category-select" className="bg-background" />}
        {f.target_type === "selected" && <CreatorPicker multiple selected={picked} onChange={setPicked} testid="broadcast-creator-picker" />}
        <div className="rounded-xl border border-primary/20 bg-primary/5 px-4 py-3 text-sm flex items-center justify-between" data-testid="broadcast-recipient-count">
          <span className="text-muted-foreground">Eligible recipients (approved creators)</span>
          <span className="num text-lg text-primary" data-testid="broadcast-recipient-count-value">{count === null ? "…" : count}</span>
        </div>
        <Field label="Title"><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="rounded-xl bg-background h-11" data-testid="broadcast-title-input" /></Field>
        <Field label="Message"><Textarea rows={5} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} className="rounded-xl bg-background" data-testid="broadcast-message-input" /></Field>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Optional link"><Input value={f.link} onChange={(e) => setF({ ...f, link: e.target.value })} placeholder="https://" className="rounded-xl bg-background h-11" data-testid="broadcast-link-input" /></Field>
          <Field label="Schedule (optional)"><Input type="datetime-local" value={f.scheduled_at} onChange={(e) => setF({ ...f, scheduled_at: e.target.value })} className="rounded-xl bg-background h-11" data-testid="broadcast-schedule-input" /></Field>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <UploadButton label={f.image ? "Replace image" : "Add image"} onUploaded={(r) => setF({ ...f, image: r.url })} testid="broadcast-image-upload" />
          {f.image && <button className="text-xs text-muted-foreground underline" onClick={() => setF({ ...f, image: "" })}>Remove image</button>}
        </div>
        <div className="flex flex-wrap gap-2 pt-2">
          <Button variant="outline" className="rounded-full" onClick={() => setPreview(!preview)} data-testid="broadcast-preview-button"><Eye className="h-4 w-4 mr-2" />Preview</Button>
          <Confirm title={`${btnLabel}?`} desc={`This message will be delivered privately to ${count} creator${count === 1 ? "" : "s"}.`} confirmLabel={btnLabel} onConfirm={send} testid="broadcast-send-confirm"
            trigger={<Button disabled={!canSend || busy} className="rounded-full" data-testid="broadcast-send-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{f.scheduled_at ? <CalendarClock className="h-4 w-4 mr-2" /> : <Send className="h-4 w-4 mr-2" />}{btnLabel}</>}</Button>} />
        </div>
      </div>
      <div className="lg:col-span-2">
        <p className="eyebrow mb-3">What creators see</p>
        {preview || f.title || f.message ? (
          <div className="uc-card p-5" data-testid="broadcast-preview">
            <p className="eyebrow text-primary mb-1">{branding.website_name}</p>
            <p className="font-medium">{f.title || "Message title"}</p>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap mt-1">{f.message || "Your message…"}</p>
            {f.image && <img src={fileUrl(f.image)} alt="" className="mt-3 rounded-xl max-h-52 object-cover" />}
            {f.link && <p className="text-xs text-primary mt-2">{f.link}</p>}
            <p className="text-[11px] text-muted-foreground/70 mt-3">{ftime(new Date().toISOString())}</p>
          </div>
        ) : <div className="uc-card p-6 text-sm text-muted-foreground">Start writing to preview. Recipients never see other recipients or counts.</div>}
      </div>
    </div>
  );
}

export default function AdminBroadcasts() {
  const [page, setPage] = useState(1);
  const { data, loading, error, reload } = useApi("/admin/broadcasts", { page });
  const cancel = async (b) => { try { await api.post(`/admin/broadcasts/${b.id}/cancel`); toast.success("Broadcast cancelled"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const target = (b) => b.target_type === "all" ? "All creators" : b.target_type === "category" ? `Category · ${b.category_name}` : "Selected creators";
  return (
    <div>
      <PageHeader eyebrow="Messaging" title="Broadcast messages" desc="Send one private message to many creators at once." />
      <Composer onSent={reload} />
      <p className="eyebrow mt-12 mb-4">Broadcast history</p>
      {loading && !data ? <LoadingGrid count={3} className="space-y-2" h="h-14" /> : error ? <ErrorState message={error} onRetry={reload} /> :
        data.items.length === 0 ? <EmptyState icon={Megaphone} title="No broadcast messages yet." testid="broadcasts-empty" /> : (
          <Table testid="broadcasts-table" head={["Title / Message", "Audience", "Recipients", "Date / Time", "Sender", "Status", ""]}>
            {data.items.map((b) => (
              <tr key={b.id} data-testid={`broadcast-row-${b.id}`}>
                <td className={`${td} max-w-xs`}><span className="block font-medium truncate">{b.title}</span><span className="block text-xs text-muted-foreground truncate">{b.message}</span></td>
                <td className={td}>{target(b)}</td>
                <td className={`${td} num`} data-testid={`broadcast-count-${b.id}`}>{b.recipient_count}</td>
                <td className={`${td} text-muted-foreground`}>{ftime(b.sent_at || b.scheduled_at || b.created_at)}</td>
                <td className={td}>{b.sender}</td>
                <td className={td}><StatusBadge status={b.status} /></td>
                <td className={td}>{b.status === "SCHEDULED" && <Button size="sm" variant="outline" className="rounded-full" onClick={() => cancel(b)} data-testid={`broadcast-cancel-${b.id}`}>Cancel</Button>}</td>
              </tr>
            ))}
          </Table>
        )}
      {data && <Pager page={page} total={data.total} onPage={setPage} />}
    </div>
  );
}
